import styled, { keyframes, css } from "styled-components";
// Path assumes this file lives at
// src/components/sections/WelcomeScreen/Welcome/Welcome.styles.js
// (matches the "WelcomeScreen/Welcome/Welcome.js" import path in App.js).
import { DIET_C, DIET_CSS_VARS } from "../../../common/colors";

// ═══════════════════════════════════════════════════════════════════════════════
// WELCOME (Screen 1) — DESIGN TOKENS
// Emerald-green Diet Planner identity — deliberately separate from the Yog
// module's saffron/gold theme. Design mirrors the Yog.styles.js pattern:
// Section carries the token block, everything else consumes CSS vars.
// ═══════════════════════════════════════════════════════════════════════════════

// ─── Keyframes ─────────────────────────────────────────────────────────────────

export const kFadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0);     }
`;

export const kFadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

export const kRiseIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0);     }
`;

// ─── Motion guard (respects prefers-reduced-motion) ──────────────────────────
const motion = (animCss) => css`
  @media (prefers-reduced-motion: no-preference) { ${animCss} }
`;

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION ROOT — design token definitions
// ═══════════════════════════════════════════════════════════════════════════════

export const Section = styled.section`
  /* ── Palette — sourced from centralized color tokens ── */
  ${DIET_CSS_VARS}

  /* ── Feature-card accents (Diet / Meals / Yoga) — local to this screen ── */
  --c-diet-fg:  var(--c-primary);
  --c-diet-bg:  ${DIET_C.secondary}2b;
  --c-meals-fg: #C96A2E;
  --c-meals-bg: #FBEADC;
  --c-yoga-fg:  #7A5AC9;
  --c-yoga-bg:  #EFEAFB;

  /* ── Radii ── */
  --r-sm: 10px;
  --r:    14px;
  --r-lg: 18px;
  --r-full: 999px;

  /* ── Shadows ── */
  --s-sm: 0 2px 10px rgba(0, 0, 0, 0.05);
  --s:    0 4px 20px rgba(0, 0, 0, 0.07);

  /* ── Spacing scale ── */
  --sp-1: 0.25rem;
  --sp-2: 0.5rem;
  --sp-3: 0.75rem;
  --sp-4: 1rem;
  --sp-5: 1.25rem;
  --sp-6: 1.5rem;
  --sp-8: 2rem;
  --sp-10: 2.5rem;

  /* ── Transitions ── */
  --t-fast: 0.15s ease;
  --t:      0.22s ease;
  --t-slow: 0.4s ease;

  position: relative;
  background: var(--c-bg);
  min-height: 100%;
`;
Section.displayName = "Section";

// ─── Page shell ────────────────────────────────────────────────────────────────

export const AppShell = styled.div`
  max-width: 1040px;
  margin: 0 auto;
  padding: var(--sp-8) var(--sp-5) var(--sp-10);

  @media (max-width: 40em) {
    padding: var(--sp-5) var(--sp-4) var(--sp-8);
  }
`;
AppShell.displayName = "AppShell";

// ═══════════════════════════════════════════════════════════════════════════════
// HEADER (kept for standalone-route use; hidden by default in Welcome.js)
// ═══════════════════════════════════════════════════════════════════════════════

export const Header = styled.header`
  background: var(--c-surface);
  border: 1px solid ${DIET_C.secondary}55;
  border-radius: var(--r-sm);
  padding: var(--sp-4) var(--sp-5);
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--sp-5);
  box-shadow: var(--s-sm);

  @media (max-width: 40em) {
    padding: var(--sp-3) var(--sp-4);
  }
`;
Header.displayName = "Header";

export const Brand = styled.span`
  font-weight: 700;
  font-size: 1.15rem;
  color: var(--c-primary);
  letter-spacing: 0.2px;

  @media (max-width: 40em) {
    font-size: 1.02rem;
  }
`;
Brand.displayName = "Brand";

export const HeaderTag = styled.span`
  font-size: 0.85rem;
  color: var(--c-text-muted);

  @media (max-width: 40em) {
    font-size: 0.75rem;
  }
`;
HeaderTag.displayName = "HeaderTag";

// ═══════════════════════════════════════════════════════════════════════════════
// BADGE (eyebrow pill above the headline)
// ═══════════════════════════════════════════════════════════════════════════════

export const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: var(--r-full);
  background: ${DIET_C.secondary}30;
  border: 1px solid ${DIET_C.secondary}70;
  color: var(--c-primary);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.2px;
  margin-bottom: var(--sp-4);
  ${motion(css`animation: ${kFadeIn} 0.5s ease both;`)}

  svg { flex-shrink: 0; }

  @media (max-width: 40em) {
    font-size: 0.72rem;
  }
`;
Badge.displayName = "Badge";

// ═══════════════════════════════════════════════════════════════════════════════
// HERO
// ═══════════════════════════════════════════════════════════════════════════════

export const Hero = styled.section`
  padding: 0;
`;
Hero.displayName = "Hero";

export const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1.7fr 1fr;
  gap: var(--sp-8);
  align-items: start;

  @media (max-width: 56em) {
    grid-template-columns: 1fr;
    gap: var(--sp-6);
  }
`;
HeroGrid.displayName = "HeroGrid";

export const HeroLeft = styled.div`
  text-align: left;
  ${motion(css`animation: ${kFadeUp} 0.5s ease both;`)}
`;
HeroLeft.displayName = "HeroLeft";

export const HeroTitle = styled.h1`
  color: var(--c-primary);
  font-size: 2.4rem;
  font-weight: 800;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.5px;

  @media (max-width: 56em) {
    font-size: 2rem;
  }

  @media (max-width: 40em) {
    font-size: 1.6rem;
  }
`;
HeroTitle.displayName = "HeroTitle";

export const TitleAccent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: var(--sp-3) 0 var(--sp-4);

  span {
    display: block;
    width: 40px;
    height: 4px;
    border-radius: var(--r-full);
    background: var(--c-primary);
  }

  em {
    display: block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--c-primary);
    opacity: 0.5;
    font-style: normal;
  }
`;
TitleAccent.displayName = "TitleAccent";

export const HeroSubhead = styled.p`
  color: var(--c-text-muted);
  font-size: 1.02rem;
  max-width: 460px;
  margin: 0 0 var(--sp-6);
  line-height: 1.6;

  @media (max-width: 40em) {
    font-size: 0.94rem;
    margin-bottom: var(--sp-5);
  }
`;
HeroSubhead.displayName = "HeroSubhead";

export const CtaButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: var(--c-primary);
  color: #fff;
  border: none;
  padding: 15px 26px;
  font-size: 1rem;
  font-weight: 700;
  border-radius: var(--r-full);
  cursor: pointer;
  transition: transform var(--t-fast), background var(--t-fast), opacity var(--t-fast), box-shadow var(--t-fast);
  box-shadow: var(--s-sm);

  svg { flex-shrink: 0; }

  .arrow {
    transition: transform var(--t-fast);
  }

  &:hover:not(:disabled) {
    background: #26692a;
    transform: translateY(-1px);
    box-shadow: var(--s);
  }

  &:hover:not(:disabled) .arrow {
    transform: translateX(3px);
  }

  &:focus-visible {
    outline: 3px solid var(--c-accent, var(--c-primary));
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 40em) {
    width: 100%;
    justify-content: center;
    padding: 15px 24px;
    font-size: 0.96rem;
  }
`;
CtaButton.displayName = "CtaButton";

// ─── Trust markers (right-column stacked cards on desktop) ───────────────────

export const TrustMarkers = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 0.4s ease, transform 0.4s ease;

  ${({ $visible }) =>
    $visible &&
    css`
      opacity: 1;
      transform: translateY(0);
    `}

  @media (max-width: 56em) {
    flex-direction: row;
    flex-wrap: wrap;
  }

  @media (max-width: 40em) {
    flex-direction: column;
  }
`;
TrustMarkers.displayName = "TrustMarkers";

export const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  background: var(--c-surface);
  border: 1px solid ${DIET_C.secondary}45;
  border-radius: var(--r-sm);
  padding: var(--sp-3) var(--sp-4);
  box-shadow: var(--s-sm);

  @media (max-width: 56em) {
    flex: 1 1 220px;
  }
`;
TrustItem.displayName = "TrustItem";

export const TrustIcon = styled.span`
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--c-diet-bg);
  color: var(--c-diet-fg);
`;
TrustIcon.displayName = "TrustIcon";

export const TrustText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
TrustText.displayName = "TrustText";

export const TrustTitle = styled.strong`
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--c-primary);
`;
TrustTitle.displayName = "TrustTitle";

export const TrustSub = styled.span`
  font-size: 0.78rem;
  color: var(--c-text-muted);
`;
TrustSub.displayName = "TrustSub";

// ═══════════════════════════════════════════════════════════════════════════════
// FEATURE CARDS
// ═══════════════════════════════════════════════════════════════════════════════

export const Features = styled.section`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--sp-4);
  margin-top: var(--sp-8);

  @media (max-width: 40em) {
    grid-template-columns: 1fr;
    gap: var(--sp-3);
    margin-top: var(--sp-6);
  }
`;
Features.displayName = "Features";

export const FeatureCard = styled.div`
  display: flex;
  flex-direction: column;
  background: var(--c-surface);
  border: 1px solid ${DIET_C.secondary}45;
  border-radius: var(--r-lg);
  padding: var(--sp-5);
  ${motion(css`animation: ${kRiseIn} 0.5s ease both;`)}
  animation-delay: ${({ $index = 0 }) => $index * 80}ms;
  transition: box-shadow var(--t-fast), transform var(--t-fast);

  &:hover {
    box-shadow: var(--s);
    transform: translateY(-2px);
  }
`;
FeatureCard.displayName = "FeatureCard";

export const FeatureHead = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  margin-bottom: var(--sp-4);
`;
FeatureHead.displayName = "FeatureHead";

export const FeatureIcon = styled.span`
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent-bg);
  color: var(--accent-fg);
`;
FeatureIcon.displayName = "FeatureIcon";

export const FeatureHeadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
FeatureHeadText.displayName = "FeatureHeadText";

export const FeatureTitle = styled.h3`
  color: var(--accent-fg);
  font-size: 1.02rem;
  font-weight: 700;
  margin: 0;
`;
FeatureTitle.displayName = "FeatureTitle";

export const FeatureSubtitle = styled.span`
  font-size: 0.8rem;
  color: var(--c-text-muted);
`;
FeatureSubtitle.displayName = "FeatureSubtitle";

export const FeatureDesc = styled.p`
  color: var(--c-text-muted);
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.55;
`;
FeatureDesc.displayName = "FeatureDesc";

export const FeatureFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: var(--sp-4);
  padding-top: var(--sp-1);
`;
FeatureFooter.displayName = "FeatureFooter";

export const FeatureArrow = styled.span`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent-bg);
  color: var(--accent-fg);
`;
FeatureArrow.displayName = "FeatureArrow";

// ─── Misc ──────────────────────────────────────────────────────────────────────

export const SessionNote = styled.p`
  text-align: center;
  font-size: 0.75rem;
  color: var(--c-text-muted);
  margin-top: var(--sp-4);
  ${motion(css`animation: ${kFadeIn} 0.3s ease both;`)}
`;
SessionNote.displayName = "SessionNote";